/* V161 — VoteForAgentWidget — 1-click vote widget for coming-soon agents.

   Friction-less alternative to ComingSoonWaitlist (which has email + note).
   Shows:
   - Big "🔥 Hlasujem za tohto agenta" CTA
   - Single-field email input (no note)
   - Real-time vote count + delta animation
   - Per-agent potential MRR display
   - Auto-redirects to ComingSoonWaitlist if user wants more detail

   Powered by existing POST /api/agent-waitlist/signup (no backend changes).
*/
import { useState, useEffect } from "react";
import axios from "axios";
import { Flame, Check, Loader2 } from "lucide-react";
import { API } from "../../config";
import ShareEarnCard from "./ShareEarnCard";

/* Read ?ref=XXXX from URL if present (visitor came from referral link) */
function readRefCode() {
  if (typeof window === "undefined") return "";
  try {
    const sp = new URLSearchParams(window.location.search);
    return (sp.get("ref") || "").trim().toUpperCase().slice(0, 16);
  } catch { return ""; }
}

export default function VoteForAgentWidget({
  agentSlug,
  agentName,
  emoji = "🤖",
  priceMonthEur = 0,
  accent = "#F59E0B",
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");
  const [voteCount, setVoteCount] = useState(null);
  const [justVoted, setJustVoted] = useState(false);

  // Fetch initial vote count
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/agent-waitlist/demand`)
      .then((r) => {
        if (cancelled) return;
        const row = (r.data?.rows || []).find((x) => x.agent_slug === agentSlug);
        setVoteCount(row?.signups || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [agentSlug]);

  // Track referral visit on mount if ?ref= is present
  useEffect(() => {
    const code = readRefCode();
    if (!code) return;
    axios
      .post(`${API}/agent-referrals/track-visit`, { code, agent_slug: agentSlug })
      .catch(() => {});
  }, [agentSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setErr("Zadaj platný email");
      return;
    }
    setState("sending");
    setErr("");
    try {
      const r = await axios.post(`${API}/agent-waitlist/signup`, {
        agent_slug: agentSlug,
        email: email.trim().toLowerCase(),
      });
      setState("done");
      setJustVoted(true);
      // Bump local counter so user sees immediate feedback
      if (!r.data?.already_signed_up) {
        setVoteCount((v) => (v == null ? 1 : v + 1));
      }
      // Track Lead in Meta Pixel + CAPI (deduped). Only on first-time signup
      // so re-voters don't inflate Lead numbers.
      if (!r.data?.already_signed_up) {
        try {
          const { trackEvent } = await import("../../lib/metaPixel");
          trackEvent("Lead", {
            email: email.trim().toLowerCase(),
            content_name: agentSlug,
            content_category: "agent_waitlist",
          });
        } catch {}
      }
      // If we came from a referral link, attribute the conversion
      const refCode = readRefCode();
      if (refCode) {
        axios
          .post(`${API}/agent-referrals/convert`, {
            code: refCode,
            converted_email: email.trim().toLowerCase(),
            agent_slug: agentSlug,
          })
          .catch(() => {});
      }
    } catch (e) {
      setState("error");
      setErr(e?.response?.data?.detail || "Nepodarilo sa zaznamenať hlas. Skús to znova.");
    }
  };

  const potentialMrr = (voteCount || 0) * priceMonthEur;

  if (state === "done") {
    return (
      <div className="space-y-3">
        <div
          className="border-2 p-5 bg-zinc-950"
          style={{ borderColor: "#10B981" }}
          data-testid="vote-widget-success"
        >
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-6 h-6 text-emerald-400" />
            <span className="text-emerald-400 font-black text-lg">Hlas zaznamenaný! 🎉</span>
          </div>
          <p className="text-zinc-300 text-sm mb-3">
            Keď bude {emoji} <strong>{agentName}</strong> LIVE, dostaneš ako prvý/á email s aktivačným linkom.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Aktuálne hlasov</div>
              <div className="text-2xl font-black text-white" data-testid="vote-count-display">
                {voteCount ?? "—"} 🔥
              </div>
            </div>
            {priceMonthEur > 0 && voteCount > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-black">Potenciálne MRR</div>
                <div className="text-2xl font-black text-emerald-400">+€{potentialMrr}/mes</div>
              </div>
            )}
          </div>
        </div>
        {/* V162 — Share & Earn card */}
        <ShareEarnCard
          voterEmail={email.trim().toLowerCase()}
          agentSlug={agentSlug}
          agentName={agentName}
          accent={accent}
        />
      </div>
    );
  }

  return (
    <div
      className="border-2 p-5 bg-zinc-950"
      style={{ borderColor: accent }}
      data-testid={`vote-widget-${agentSlug}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5" style={{ color: accent }} />
          <span className="font-black text-base text-white">Hlasuj za tohto agenta</span>
        </div>
        {voteCount != null && (
          <div className="text-right" data-testid="vote-counter">
            <div className="text-xl font-black text-white">
              {voteCount} <span className="text-base">🔥</span>
            </div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">hlasov</div>
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
        Čím viac hlasov, tým skôr postavíme. Keď bude LIVE, dostaneš automaticky email a 14-day trial.
      </p>

      <form onSubmit={submit} className="space-y-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tvoj@email.sk"
          required
          disabled={state === "sending"}
          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none"
          data-testid="vote-email-input"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          style={{ background: accent, color: "#000" }}
          className="w-full py-3 font-black text-sm uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="vote-submit-btn"
        >
          {state === "sending" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Zaznamenávam...
            </>
          ) : (
            <>
              <Flame className="w-4 h-4" /> Hlasujem za tohto agenta
            </>
          )}
        </button>
        {err && (
          <div className="text-red-400 text-xs" data-testid="vote-error">{err}</div>
        )}
      </form>

      {priceMonthEur > 0 && voteCount > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
            Ak shippneme · potenciálne MRR
          </span>
          <span className="text-sm font-black text-emerald-400">
            +€{potentialMrr}/mes
          </span>
        </div>
      )}
    </div>
  );
}
